import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { JobsService } from '../services/jobs.service';
import { UserIdService } from '../services/user-id.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';

/**
 * This guard is used across all app routes that require live data,
 * determining if the auth token is valid before allowing navigation to occur
 * Updated to support both MSAL and legacy OAuth authentication
 */
@Injectable()
export class AuthenticationGuard  {

	/** Creates an instance of the AuthenticationGuard
	 *
	 * @param router used for navigation actions
	 * @param msalService MSAL service for Microsoft authentication
	 */
	constructor(
		private readonly router: Router, 
		private readonly idService: UserIdService, 
		private readonly jobService: JobsService,
		private readonly msalService: MsalService
	) {
	}

	/** the actual guard for routing. Uses the current token information to determine if the user needs to go through the login flow again
	 *
	 * @param route the snapshot of the current ActivatedRoute
	 * @param state the snapshot of the current RouterState
	 * @returns whether route can be activated or not
	 */
	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		// Store state url in sessionStorage for later redirect by callback component
		sessionStorage['redirectURL'] = state.url;
		
		// Check if we're processing an MSAL redirect
		if (this.isMsalEnabled()) {
			const msalProcessing = sessionStorage.getItem('msalProcessing');
			const redirectComplete = sessionStorage.getItem('msalRedirectComplete');
			
			// Allow navigation if we're processing a redirect or redirect just completed
			if (msalProcessing === 'true' || redirectComplete === 'true') {
				console.log('Guard: MSAL processing/redirect detected, allowing navigation');
				return true;
			}
			
			// Check MSAL authentication
			const msalAuth = this.checkMsalAuth();
			console.log('Guard: MSAL auth check result:', msalAuth);
			
			if (msalAuth) {
				return true;
			}
			
			// No MSAL authentication, need to login
			console.log('Guard: No MSAL authentication, redirecting to login');
			this.router.navigate(['/']);
			return false;
		}
		
		// Fall back to legacy OAuth check
		return this.checkLogin();
	}

	/**
	 * Check if MSAL is enabled in environment
	 */
	private isMsalEnabled(): boolean {
		// Check if useMsal flag exists and is true
		return environment['useMsal'] === true;
	}

	/**
	 * Check if user is authenticated via MSAL
	 * @returns boolean indicating if user is authenticated
	 */
	private checkMsalAuth(): boolean {
		// First check if there's an active account
		const activeAccount = this.msalService.instance.getActiveAccount();
		if (activeAccount) {
			console.log('Guard: Active account found');
			return true;
		}
		
		// If no active account, check all accounts
		const accounts = this.msalService.instance.getAllAccounts();
		console.log('Guard: Number of accounts:', accounts.length);
		
		if (accounts.length > 0) {
			this.msalService.instance.setActiveAccount(accounts[0]);
			console.log('Guard: Set active account successfully');
			return true;
		}
		
		console.log('Guard: No MSAL accounts found');
		return false;
	}

	/** Function used to determine if the current token is valid. Checks token expiration against the current timestamp
	 * @returns whether or not the token is expired
	 */
	isTokenExpired(): boolean {
		const epoch = Math.trunc(new Date().getTime() / 1000);
		let expEpoch = null;

		if (sessionStorage.getItem('tokenExp') && sessionStorage.getItem('tokenExp') !== 'null') {
			expEpoch = parseInt(sessionStorage.getItem('tokenExp'), 10);
			return (epoch >= expEpoch);
		} else {
			return true;
		}
	}

	/** Uses isTokenExpired() to determine if the user credentials should be cleared and the user forwarded to the login component
	 * @returns for whether the user is "logged in" or not
	 */
	checkLogin(): boolean {
		// If using MSAL, check MSAL authentication instead of old OAuth tokens
		if (this.isMsalEnabled()) {
			return this.checkMsalAuth();
		}
		
		// Check if token is expired (or null)
		if (this.isTokenExpired()) {
			// Clear oauth session items
			sessionStorage.removeItem('tokenExp');
			sessionStorage.removeItem('tokenIssue');
			sessionStorage.removeItem('strAccessToken');
			sessionStorage.removeItem('encodedAccessToken');
			sessionStorage.removeItem("viewlog");
			this.idService.setUserID('Not Logged In');

			// Navigate to the login page with extras
			this.router.navigate(['/']);

			return false; // guard complete and then router redirects to /login
		}		
		return true;
	}
	
}
